import { createTheme, alpha } from "@mui/material/styles";
import { ExpandMore } from "@mui/icons-material";

const breakpoints = {
  values: {
    xs: 0,
    sm: 768,
    md: 1025,
    lg: 1230,
    xl: 1920
  }
};

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    display1: true;
    display2: true;
    display3: true;
    sectionTitle: true;
    lead: true;
  }
}

declare module "@mui/material/styles/createTheme" {
  interface Theme {
    spacer_xs: string;
    spacer_s: string;
    spacer_n: string;
    spacer_m: string;
    spacer_l: string;
    spacer_xxl: string;
  }
  // allow configuration using `createMuiTheme`
  interface ThemeOptions {
    spacer_xs?: string;
    spacer_s?: string;
    spacer_n?: string;
    spacer_m?: string;
    spacer_l?: string;
    spacer_xxl?: string;
  }
}

declare module "@mui/material/styles" {
  interface Palette {
    fuchsia: Palette["primary"];
    fuchsiaAccessible: Palette["primary"];
    fuchsiaLight: Palette["primary"];
    blue: Palette["primary"];
    blueLight: Palette["primary"];
    violet: Palette["primary"];
    violetLight: Palette["primary"];
    purple: Palette["primary"];
    white: Palette["primary"];
    black: Palette["primary"];
    blackLight: Palette["primary"];
    red: Palette["primary"];
    ochre: Palette["primary"];
    eggShell: Palette["primary"];
    disabled: Palette["primary"];
    sideBackdrop: Palette["primary"];
    backdrop: Palette["primary"];
  }
  interface PaletteOptions {
    fuchsia: Palette["primary"];
    fuchsiaAccessible: Palette["primary"];
    fuchsiaLight: Palette["primary"];
    blue: Palette["primary"];
    blueLight: Palette["primary"];
    violet: Palette["primary"];
    violetLight: Palette["primary"];
    purple: Palette["primary"];
    white: Palette["primary"];
    black: Palette["primary"];
    blackLight: Palette["primary"];
    red: Palette["primary"];
    ochre: Palette["primary"];
    eggShell: Palette["primary"];
    disabled: Palette["primary"];
    backdrop: Palette["primary"];
  }
}

let baseTheme = createTheme({ breakpoints });
let { palette } = createTheme();

let theme = createTheme({
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          fontSize: "62.5%",
          height: "100%"
        },
        body: {
          height: "100%"
        }
      }
    }
  },
  spacer_xs: "0.2rem",
  spacer_s: "0.8rem",
  spacer_n: "1.6rem",
  spacer_m: "2.4rem",
  spacer_l: "4rem",
  spacer_xxl: "10.4rem",
  breakpoints,
  typography: {
    fontFamily: ["OpenSans", "Arial"].join(","),
    display1: {
      fontSize: "4.4em",
      fontWeight: "bold",
      letterSpacing: "normal",
      lineHeight: 1.1,
      [baseTheme.breakpoints.up("sm")]: {
        fontSize: "7.2rem",
        letterSpacing: "-1px"
      },
      [baseTheme.breakpoints.up("md")]: {
        fontSize: "10em",
        letterSpacing: "-2px"
      }
    },
    display2: {
      fontSize: "3.6em",
      fontWeight: "bold",
      letterSpacing: "normal",
      lineHeight: 1.1,
      [baseTheme.breakpoints.up("sm")]: {
        fontSize: "4.8rem"
      },
      [baseTheme.breakpoints.up("md")]: {
        fontSize: "6em"
      }
    },
    display3: {
      fontSize: "3.2em",
      fontWeight: "bold",
      letterSpacing: "normal",
      lineHeight: 1.1,
      [baseTheme.breakpoints.up("sm")]: {
        fontSize: "3.6rem"
      },
      [baseTheme.breakpoints.up("md")]: {
        fontSize: "4em"
      }
    },
    h1: {
      fontSize: "4rem",
      fontWeight: "bold",
      letterSpacing: "normal",
      lineHeight: 1.1,
      [baseTheme.breakpoints.up("sm")]: {
        fontSize: "6rem",
        letterSpacing: "-1px"
      },
      [baseTheme.breakpoints.up("md")]: {
        fontSize: "8rem",
        letterSpacing: "-2px"
      }
    },
    h2: {
      fontSize: "2.4rem",
      fontWeight: "bold",
      letterSpacing: "normal",
      lineHeight: 1.2,
      [baseTheme.breakpoints.up("sm")]: {
        fontSize: "2.8rem"
      },
      [baseTheme.breakpoints.up("md")]: {
        fontSize: "3.2rem"
      }
    },
    h3: {
      fontSize: "2rem",
      fontWeight: "bold",
      letterSpacing: "normal",
      lineHeight: 1.2,
      [baseTheme.breakpoints.up("sm")]: {
        fontSize: "2.3rem"
      },
      [baseTheme.breakpoints.up("md")]: {
        fontSize: "2.8rem"
      }
    },
    h4: {
      fontSize: "1.6rem",
      fontWeight: "bold",
      letterSpacing: "normal",
      lineHeight: 1.2,
      [baseTheme.breakpoints.up("sm")]: {
        fontSize: "1.8rem"
      },
      [baseTheme.breakpoints.up("md")]: {
        fontSize: "2rem"
      }
    },
    lead: {
      fontSize: "2rem",
      fontWeight: "normal",
      letterSpacing: "normal",
      lineHeight: 1.7
    },
    sectionTitle: {
      fontSize: "1.8rem",
      fontWeight: "bold",
      letterSpacing: "normal",
      lineHeight: "2.3rem"
    },
    body1: {
      fontSize: "1.6rem",
      fontWeight: "normal",
      letterSpacing: "normal",
      lineHeight: 1.7
    },
    caption: {
      fontSize: "1.2rem",
      fontWeight: "normal",
      letterSpacing: "normal",
      lineHeight: 1.7
    }
  } as any,
  palette: {
    primary: {
      main: "#005078"
    },
    secondary: {
      main: "#3241BE"
    },
    background: {
      paper: "#f7f7fc",
      default: "#f7f7fc"
    },
    text: {
      primary: "#1D1C1D"
    },
    success: {
      main: "#25b830"
    },
    error: {
      main: "#E90C43"
    },
    action: {
      disabled: "#FFFFFF",
      disabledBackground: "#C2C4D6"
    },
    fuchsia: palette.augmentColor({
      color: {
        main: "#f50aa0"
      }
    }),
    fuchsiaAccessible: palette.augmentColor({
      color: {
        main: "#005078"
      }
    }),
    fuchsiaLight: palette.augmentColor({
      color: {
        main: "#fef0f9"
      }
    }),
    blue: palette.augmentColor({
      color: {
        main: "#3241BE"
      }
    }),
    blueLight: palette.augmentColor({
      color: {
        main: "#f0f1ff"
      }
    }),
    violet: palette.augmentColor({
      color: {
        main: "#7d32ad"
      }
    }),
    violetLight: palette.augmentColor({
      color: {
        main: "#f9f0ff"
      }
    }),
    purple: palette.augmentColor({
      color: {
        main: "#c823be"
      }
    }),
    white: palette.augmentColor({
      color: {
        main: "#FFFFFF"
      }
    }),
    black: palette.augmentColor({
      color: {
        main: "#1d1c1d",
        dark: "#000000"
      }
    }),
    blackLight: palette.augmentColor({
      color: {
        main: "#303030"
      }
    }),
    red: palette.augmentColor({
      color: {
        main: "#ee0000",
        dark: "#ff0000"
      }
    }),
    ochre: palette.augmentColor({
      color: {
        main: "#ffca6a"
      }
    }),
    eggShell: palette.augmentColor({
      color: {
        main: "#fffaf0"
      }
    }),
    disabled: palette.augmentColor({
      color: {
        main: "#c2c4d6"
      }
    }),
    backdrop: palette.augmentColor({
      color: {
        main: "rgba(39, 0, 64, 0.8)"
      }
    })
  } as any
});

declare module "@mui/material/Paper" {
  interface PaperPropsVariantOverrides {
    section: true;
  }
}

theme = createTheme(theme, {
  components: {
    MuiPaper: {
      variants: [
        {
          props: { variant: "section" },
          style: {
            backgroundColor: theme.palette.white.main,
            borderRadius: "0.8rem",
            padding: "3.2rem 1.6rem",
            marginBottom: theme.spacer_m,
            boxShadow: `0px 2px 4px 0px ${alpha(theme.palette.blue.main, 0.1)}`,
            [theme.breakpoints.up("sm")]: {
              padding: "3.2rem 2.4rem"
            }
          }
        }
      ]
    },
    MuiAccordion: {
      defaultProps: {
        elevation: 0
      }
    },
    MuiInputLabel: {
      variants: [
        {
          props: { variant: "standard" },
          style: {
            color: theme.palette.black.main,
            fontSize: "1.6rem",
            fontWeight: "700",
            "&.Mui-focused:not(.Mui-error)": {
              color: theme.palette.black.main
            },
            "&.MuiInputLabel-shrink": {
              fontWeight: 400,
              fontSize: "2.13rem"
            },
            "&[data-shrink=false]+.MuiInputBase-formControl .MuiInputBase-input-MuiInput-input":
              {
                "&:before": {
                  borderBottom: `0.6rem solid red`
                },
                "&:after": {
                  borderBottom: `0.6rem solid green`
                }
              },
            "&.MuiInputLabel-shrink + .MuiInput-root": {
              "&:before": {
                borderBottom: `0.2rem solid ${theme.palette.fuchsiaAccessible.main}`
              }
            }
          }
        }
      ]
    },
    MuiTextField: {
      defaultProps: {
        variant: "standard",
        fullWidth: true
      },
      variants: [
        {
          props: { variant: "standard" },
          style: {
            "& .MuiInput-root": {
              color: theme.palette.black.main,
              fontSize: "1.6rem",
              fontWeight: "700",
              "&:before": {
                borderBottom: `0.2rem solid ${theme.palette.black.main}`
              },
              "&.Mui-disabled": {
                "&:before": {
                  borderBottom: `0.2rem solid ${theme.palette.disabled.main}`
                }
              },
              "&.Mui-focusVisible, &.Mui-active": {
                "&:after": {
                  borderBottom: `0.2rem solid ${theme.palette.blue.main}`
                }
              },
              "& .MuiSelect-select": {
                "&:focus": {
                  backgroundColor: "transparent"
                }
              }
            }
          }
        },
        {
          props: { variant: "filled" },
          style: {
            "input::-ms-reveal,input::-ms-clear": {
              display: "none"
            },
            "& .MuiInputLabel-root": {
              paddingLeft: "17px"
            },
            "& .MuiFilledInput-root": {
              overflow: "hidden",
              borderRadius: "27.5px",
              backgroundColor: theme.palette.white.main,
              marginBottom: "1em",
              "&:before": {
                display: "none"
              },
              "& input.MuiFilledInput-input": {
                padding: "25px 24px  8px",
                backgroundColor: `${theme.palette.white.main} !important`
              },
              "&.Mui-error input.MuiFilledInput-input": {
                color: theme.palette.error.main
              },
              "&:hover": {
                backgroundColor: theme.palette.white.main
              },
              "& .MuiIconButton-root": {
                padding: "23px",
                color: theme.palette.fuchsiaAccessible.main,
                "&:focus": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)"
                },
                "&:hover": {
                  backgroundColor: "unset"
                },
                "& .MuiSvgIcon-root": {
                  fontSize: "20px"
                }
              }
            },
            "& .MuiFormHelperText-root.Mui-error": {
              padding: "12px",
              fontSize: "1.3rem",
              borderRadius: "8px",
              color: theme.palette.white.main,
              backgroundColor: theme.palette.fuchsiaAccessible.main,
              marginBottom: "1em",
              position: "relative",
              span: {
                display: "block"
              },
              "&:before": {
                content: '""',
                width: 0,
                height: 0,
                position: "absolute",
                borderLeft: "10px solid transparent",
                borderRight: "10px solid transparent",
                borderBottom: `10px solid ${theme.palette.fuchsiaAccessible.main}`,
                top: "-10px",
                marginLeft: "40px"
              }
            }
          }
        }
      ]
    },
    MuiSelect: {
      defaultProps: {
        variant: "filled",
        IconComponent: ExpandMore
      },
      variants: [
        {
          props: { variant: "filled" },
          style: {
            overflow: "hidden",
            borderRadius: "27.5px",
            paddingLeft: "17px",
            backgroundColor: theme.palette.white.main,
            marginBottom: "1em",
            "&:before": {
              display: "none"
            }
          }
        }
      ]
    },
    MuiButton: {
      defaultProps: {
        disableFocusRipple: true,
        disableElevation: true
      },
      variants: [
        {
          props: { variant: "outlined" },
          style: {
            backgroundColor: theme.palette.white.main,
            textTransform: "none",
            fontSize: "1.6rem",
            fontWeight: "700",
            color: theme.palette.fuchsiaAccessible.main,
            border: `0.1rem solid ${theme.palette.fuchsiaAccessible.main}`,
            borderRadius: "2.75rem",
            lineHeight: "2.3rem",
            padding: "1.5rem 4rem",
            "&.Mui-disabled": {
              color: theme.palette.disabled.main
            },
            "&:hover": {
              backgroundColor: theme.palette.white.main,
              boxShadow: `0 0 0.5rem ${theme.palette.fuchsiaAccessible.main}`,
              border: `0.1rem solid ${theme.palette.fuchsiaAccessible.main}`
            },
            "&.Mui-focusVisible, &.Mui-active": {
              boxShadow: `0 0 0.5rem ${theme.palette.white.main}`,
              outlineOffset: "0.2rem",
              outline: `0.2rem solid ${theme.palette.blue.main}`
            },
            "& .MuiTouchRipple-child": {
              backgroundColor: theme.palette.fuchsiaAccessible.main
            }
          }
        },
        {
          props: { variant: "outlined", size: "small" },
          style: {
            lineHeight: "2.2rem",
            padding: "0.7rem 4rem"
          }
        },
        {
          props: { variant: "contained" },
          style: {
            backgroundColor: theme.palette.fuchsiaAccessible.main,
            textTransform: "none",
            fontSize: "1.6rem",
            fontWeight: "700",
            color: theme.palette.white.main,
            border: `0.1rem solid ${theme.palette.fuchsiaAccessible.main}`,
            borderRadius: "2.75rem",
            lineHeight: "2.3rem",
            padding: "1.5rem 4rem",
            "&.Mui-disabled": {
              border: `0.1rem solid ${theme.palette.disabled.main}`
            },
            "&:hover": {
              backgroundColor: theme.palette.fuchsiaAccessible.main,
              boxShadow: `0 0 0.5rem ${theme.palette.white.main}`,
              border: `0.1rem solid ${theme.palette.fuchsiaAccessible.main}`
            },
            "&.Mui-focusVisible, &.Mui-active": {
              boxShadow: `0 0 0.5rem ${theme.palette.fuchsiaAccessible.main}`,
              outlineOffset: "0.2rem",
              outline: `0.2rem solid ${theme.palette.blue.main}`
            }
          }
        }
      ]
    },
    MuiTab: {
      defaultProps: {
        disableFocusRipple: true
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          margin: 0,
          borderRadius: "8px",
          fontSize: "1.6rem",
          "& .MuiAlert-icon": {
            margin: 0,
            fontSize: 30,
            marginRight: "10px",
            padding: "16px",
          },
          "& .MuiAlert-message": {
            paddingTop: "18px"
          }
        }
      }
    }
  }
});

export default theme;
